import type { AccessibleTextPayload, CanvasElement, SchoolBrand } from './index';
import type { ResolvedElement } from './preview';

export type JPEGQuality = 'low' | 'medium' | 'high' | 'maximum';

export const JPEG_QUALITY_MAP: Record<JPEGQuality, number> = {
  low: 0.6,
  medium: 0.8,
  high: 0.92,
  maximum: 1.0,
};

export type ExportFormat = 'jpeg' | 'png' | 'webp';

export const EXPORT_FORMAT_MIME: Record<ExportFormat, string> = {
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

export interface ExportConfig {
  format: ExportFormat;
  quality: JPEGQuality;
  scale: number;
  includeOverlay: boolean;
  includeBackground: boolean;
  includeComposite?: boolean;
  recordIndex?: number;
  filenamePrefix?: string;
}

export const DEFAULT_EXPORT_CONFIG: ExportConfig = {
  format: 'jpeg',
  quality: 'high',
  scale: 1,
  includeOverlay: true,
  includeBackground: true,
};

export type ExportJobStatus =
  | 'pending'
  | 'resolving'
  | 'rendering'
  | 'generating'
  | 'uploading'
  | 'complete'
  | 'failed';

export interface ExportJobProgress {
  status: ExportJobStatus;
  percent: number;
  message: string;
  error?: string;
}

export interface ExportJob {
  id: string;
  projectId: string;
  templateId?: string;
  config: ExportConfig;
  progress: ExportJobProgress;
  startedAt: Date;
  completedAt?: Date;
  outputs?: ExportOutputs;
}

export interface ExportOutputs {
  backgroundUrl?: string;
  overlayUrl?: string;
  compositeUrl?: string;
  backgroundBlob?: Blob;
  overlayBlob?: Blob;
  compositeBlob?: Blob;
  dimensions: {
    width: number;
    height: number;
  };
  fileSizes: {
    background?: number;
    overlay?: number;
    composite?: number;
  };
}

export interface OverlayElement {
  id: string;
  type: 'text' | 'button' | 'shape' | 'image';
  styles: Record<string, string | number>;
  textContent?: string;
  svgContent?: string;
  href?: string;
  altText?: string;
  ariaLabel?: string;
  tabIndex?: number;
}

export interface OverlayManifest {
  version: string;
  meta: {
    projectId: string;
    projectName: string;
    templateId?: string;
    exportedAt: string;
    dimensions: { width: number; height: number };
  };
  elements: OverlayElement[];
  containerClass: string;
}

export interface ExportRecord {
  id: string;
  project_id: string;
  template_id: string | null;
  created_by: string;
  created_at: string;
  export_config: ExportConfig;
  background_url: string | null;
  overlay_url: string | null;
  composite_url: string | null;
  file_sizes: { background?: number; overlay?: number; composite?: number };
  dimensions: { width: number; height: number };
  status: 'complete' | 'failed';
  error_message: string | null;
  metadata: Record<string, unknown>;
}

export function getExportStoragePath(
  projectId: string,
  exportId: string,
  filename: string
): string {
  return `${projectId}/${exportId}/${filename}`;
}

export function getBackgroundFilename(format: ExportFormat, scale: number): string {
  const scaleStr = scale > 1 ? `@${scale}x` : '';
  return `background${scaleStr}.${format}`;
}

export function getOverlayFilename(): string {
  return 'overlay.txt';
}

export function getCompositeFilename(format: ExportFormat, scale: number): string {
  const scaleStr = scale > 1 ? `@${scale}x` : '';
  return `composite${scaleStr}.${format}`;
}

export function getScaleSuffix(scale: number): string {
  return scale > 1 ? `@${scale}x` : '';
}

// ═══════════════════════════════════════
// Packet Export Types (Downstream Packet Tool)
// ═══════════════════════════════════════

export interface HeroConfig {
  id: string;
  schoolId: string;
  backgroundImage: string;
  canvasPx: {
    desktop: { width: number; height: number };
  };
  canvasRatio: {
    desktop: string;
  };
  /**
   * Accessible text — ordered, resolved plain-text strings for visible
   * text-bearing elements (text/button), in the project's reading order.
   * Additive-only: absent on legacy packets and on projects with no
   * readable elements. See src/lib/readingOrder.ts and AccessibleTextPayload
   * in src/types/index.ts. Epic: cwt-containment-accessible-reading-order (el-ax-1).
   */
  accessibleText?: AccessibleTextPayload;
}

export type PacketLayerType = 'text' | 'container-text' | 'link' | 'image' | 'shape';
export type PacketHtmlTag = 'h1' | 'h2' | 'h3' | 'p' | 'a' | 'div' | 'img';

export interface ExportLayer {
  id: string;
  type: PacketLayerType;
  content: string;
  htmlTag: PacketHtmlTag;
  url?: string;
  src?: string;
  alt?: string;
  svgContent?: string;
  ariaLabel?: string;
  ariaRole?: string;
  ariaHidden?: boolean;
  responsive: {
    desktop: {
      rawCSS: string;
    };
  };
}

export interface ExportPacket {
  heroConfig: HeroConfig;
  layers: ExportLayer[];
}

export interface ButtonUrlAssignment {
  elementId: string;
  elementName: string;
  url: string;
}

export interface PacketExportOptions {
  projectName: string;
  legacyId: string;
  storeId?: string;
  buttonUrls: ButtonUrlAssignment[];
  canvasWidth: number;
  canvasHeight: number;
  backgroundColor: string;
}

export interface PacketExportResult {
  success: boolean;
  filename: string;
  backgroundBlob?: Blob;
  overlayJson?: string;
  error?: string;
}

export const UNBRANDED_LEGACY_ID = '0000';

export function generatePacketFilename(
  legacyId: string | null | undefined,
  projectName: string,
  storeId: string = '0'
): string {
  const sanitized = projectName
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .replace(/-+/g, '-');
  const effectiveId = (legacyId && legacyId.trim()) ? legacyId.trim() : UNBRANDED_LEGACY_ID;
  return `${effectiveId}_${sanitized}_${storeId}`;
}

// ═══════════════════════════════════════
// Bulk Export Types
// ═══════════════════════════════════════

export interface FilenamePattern {
  template: string;
  separator: string;
}

export const DEFAULT_FILENAME_PATTERN: FilenamePattern = {
  template: '{subdomain}_{preset}.{format}',
  separator: '_',
};

export const DEFAULT_OVERLAY_FILENAME_PATTERN: FilenamePattern = {
  template: '{subdomain}_{preset}.js',
  separator: '_',
};

export interface ExportPreset {
  id: string;
  label: string;
  description: string;
  config: ExportConfig;
}

export const EXPORT_PRESETS: ExportPreset[] = [
  {
    id: 'web-hero',
    label: 'Web Hero',
    description: 'JPEG background + JS overlay for ADA-compliant hero banners',
    config: {
      format: 'jpeg',
      quality: 'high',
      scale: 1,
      includeOverlay: true,
      includeBackground: true,
    },
  },
  {
    id: 'web-image',
    label: 'Web Image',
    description: 'Single JPEG background — no overlay, for static placements',
    config: {
      format: 'jpeg',
      quality: 'high',
      scale: 1,
      includeOverlay: false,
      includeBackground: true,
    },
  },
  {
    id: 'print-asset',
    label: 'Print Asset',
    description: 'High-resolution PNG at 3x for print use',
    config: {
      format: 'png',
      quality: 'maximum',
      scale: 3,
      includeOverlay: false,
      includeBackground: true,
    },
  },
];

export interface BulkExportTarget {
  brandId: string;
  schoolId: string;
  schoolName: string;
  subdomain: string | null;
  brand: SchoolBrand;
}

export type BulkItemStatus = 'pending' | 'rendering' | 'complete' | 'failed' | 'skipped';

export interface BulkExportItemProgress {
  brandId: string;
  status: BulkItemStatus;
  percent: number;
  message: string;
  error?: string;
  outputs?: ExportOutputs;
}

export type BulkExportJobStatus =
  | 'idle'
  | 'running'
  | 'paused'
  | 'complete'
  | 'cancelled'
  | 'failed';

export interface BulkExportJob {
  id: string;
  projectId: string;
  templateId?: string;
  presetId: string;
  config: ExportConfig;
  filenamePattern: FilenamePattern;
  targets: BulkExportTarget[];
  items: BulkExportItemProgress[];
  status: BulkExportJobStatus;
  startedAt: Date | null;
  completedAt: Date | null;
  totalCount: number;
  completedCount: number;
  failedCount: number;
  zipBlobUrl?: string;
}

export interface BulkExportRecord {
  id: string;
  project_id: string;
  template_id: string | null;
  created_by: string;
  created_at: string;
  preset_id: string;
  export_config: ExportConfig;
  filename_pattern: FilenamePattern;
  total_brands: number;
  completed_brands: number;
  failed_brands: number;
  zip_url: string | null;
  status: 'complete' | 'partial' | 'failed';
  error_message: string | null;
  metadata: Record<string, unknown>;
}

export function resolveFilenamePattern(
  pattern: FilenamePattern,
  brand: BulkExportTarget,
  preset: string,
  format: ExportFormat,
  index: number
): string {
  const now = new Date();
  const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;

  let result = pattern.template
    .replace(/\{subdomain\}/g, brand.subdomain || 'unknown')
    .replace(/\{school_name\}/g, brand.schoolName.replace(/[^a-zA-Z0-9]/g, pattern.separator))
    .replace(/\{brand_id\}/g, brand.brandId.slice(0, 8))
    .replace(/\{preset\}/g, preset)
    .replace(/\{format\}/g, format)
    .replace(/\{index\}/g, String(index))
    .replace(/\{timestamp\}/g, timestamp);

  result = result.replace(/[<>:"/\\|?*]+/g, '_');
  return result;
}
